import ArrowShare from 'shared-svg/ArrowShare.svg'
import Linked from 'shared-components/service/Linked'
import { CommonDataContext } from 'shared-components/contexts/CommonDataContext'
import StoryCard, { StorySkeleton } from './StoryCard'
import styles from './HeaderPanels.module.css'
import { EDITORIAL_COLUMNS, EDITORIAL_STORIES_PER_COLUMN } from 'lib/header-nav/config'
import type { HeaderNavEditorialColumn } from 'lib/header-nav/types'

interface EditorialPanelProps {
  columns: HeaderNavEditorialColumn[]
  /** True while the document is still being fetched — drives the skeletons. */
  loading: boolean
  onNavigate: () => void
}

/**
 * The Editorial panel: News · Opinions & Features · Reviews, three stories each, over a
 * strip of company links and the advertise card.
 *
 * This is where the five editorial hubs of the old second nav row went — the header
 * review's turn-4 decision. Company links and the advertise CTA come from WP's common
 * data (`menuFooter` / `advertiseButton`) rather than being hardcoded here, so an editor
 * changing them in WP changes them here too.
 */
const EditorialPanel: React.VFC<EditorialPanelProps> = ({ columns, loading, onNavigate }) => {
  const {
    common: { menuFooter, advertiseButton },
  } = CommonDataContext.useContainer()

  // Fall back to the configured hubs so the columns exist (headed, linked, countless)
  // before the document lands or if it never does.
  const rendered: HeaderNavEditorialColumn[] =
    columns.length > 0
      ? columns
      : EDITORIAL_COLUMNS.map(hub => ({
          id: hub.id,
          label: hub.label,
          href: hub.path,
          total: null,
          stories: [],
        }))

  return (
    <div className={styles.inner}>
      <div className={styles.columnsThree}>
        {rendered.map(column => (
          <div key={column.id} className={styles.column}>
            <div className={styles.columnHead}>
              <span className={styles.columnTitle}>{column.label}</span>
              <Linked href={column.href} className={styles.columnAll} onClick={onNavigate}>
                {column.total !== null ? `All ${column.total.toLocaleString('en-US')} →` : 'All →'}
              </Linked>
            </div>
            {column.stories.map(story => (
              <StoryCard
                key={story.url}
                story={story}
                meta="author"
                variant="editorial"
                onNavigate={onNavigate}
              />
            ))}
            {column.stories.length === 0 &&
              loading &&
              Array.from({ length: EDITORIAL_STORIES_PER_COLUMN }, (_, i) => (
                <StorySkeleton key={i} variant="editorial" />
              ))}
          </div>
        ))}
      </div>

      <div className={styles.footer}>
        <div className={styles.footerLinks}>
          <span className={styles.footerLabel}>{'SoundGuys'}</span>
          {menuFooter.map(item => (
            <Linked
              key={item.url}
              href={item.url}
              className={styles.footerLink}
              onClick={onNavigate}
            >
              {item.title}
            </Linked>
          ))}
        </div>
        <Linked {...advertiseButton.pLink} className={styles.advertise} onClick={onNavigate}>
          <span>
            <span className={styles.advertiseTitle}>{advertiseButton.label}</span>
            <span className={styles.advertiseMeta}>
              {'2M+ monthly audio audience · Authority Media'}
            </span>
          </span>
          <span className={styles.advertiseArrow} aria-hidden="true">
            <ArrowShare className={styles.advertiseArrowIcon} />
          </span>
        </Linked>
      </div>
    </div>
  )
}

export default EditorialPanel
